import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Divider,
  Chip,
  Alert
} from '@mui/material';
import { useQuery, gql } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const GET_DAY_EVENTS = gql`
  query GetDayEvents($dateRange: DateRange!) {
    getCalendarEvents(dateRange: $dateRange) {
      id
      title
      description
      startTime
      endTime
      location
      color
      allDay
    }
  }
`;

interface DayEventsProps {
  selectedDate: Dayjs;
}

const DayEvents: React.FC<DayEventsProps> = ({ selectedDate }) => {
  const dateStart = selectedDate.startOf('day').toISOString();
  const dateEnd = selectedDate.endOf('day').toISOString();

  const { data, loading, error } = useQuery(GET_DAY_EVENTS, {
    variables: { dateRange: { start: dateStart, end: dateEnd } },
    fetchPolicy: 'cache-and-network'
  });

  if (loading && !data) return null;
  if (error) return <Alert severity="error">Failed to load events: {error.message}</Alert>;

  const events = data?.getCalendarEvents || [];

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Events
      </Typography>

      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
          No events scheduled for today.
        </Typography>
      ) : (
        <List sx={{ pt: 0 }}>
          {events.map((event: any) => (
            <React.Fragment key={event.id}>
              <ListItem 
                disablePadding
                sx={{ py: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: event.color || 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {event.title}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption">
                      {event.allDay ? 'All Day' : `${dayjs(event.startTime).format('h:mm A')} - ${dayjs(event.endTime).format('h:mm A')}`}
                    </Typography>
                  </Box>
                  
                  {event.location && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                      <LocationOnIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption" noWrap>
                        {event.location}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {event.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2.5, display: 'block' }}>
                    {event.description}
                  </Typography>
                )}
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default DayEvents;
